import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTag,
  faPlus,
  faPenToSquare,
  faTrashAlt,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

const LIST_URL = "mst-admin/promo-codes";
const CREATE_URL = "mst-admin/promo-codes";
const UPDATE_URL = "mst-admin/promo-codes/";
const DELETE_URL = "mst-admin/promo-codes/";

// Must match the backend's purchasable pack types exactly (initiatePackPurchase.js
// VALID_PACK_TYPES). The old CONNECT_UNLOCK / ALIGNMENT_UNLOCK keys were NOT real
// pack types — selecting them restricted a code to zero redeemable packs, so it
// silently never worked. Single-unlock's packType is 'single_unlock'.
const PLANS = ["annual", "premium", "elite", "spark_bundle", "single_unlock"];

const EMPTY_FORM = {
  code: "",
  discountType: "fixed",
  discountValue: "",
  maxUses: "",
  perUserLimit: "1",
  influencer: "",
  upiHandle: "",
  commissionRate: "10",
  eligiblePlans: [],
  expiresAt: "",
};

const PromoCodeList = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    const res = await func.get(LIST_URL);
    if (res?.status === 200) setCodes(res.data?.codes || []);
    else toast.error("Failed to load promo codes");
    setLoading(false);
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditId(c._id);
    setForm({
      code: c.code,
      discountType: c.discountType,
      discountValue: String(c.discountValue),
      maxUses: String(c.maxUses ?? ""),
      perUserLimit: String(c.perUserLimit ?? "1"),
      influencer: c.influencer || "",
      upiHandle: c.upiHandle || "",
      commissionRate: String(c.commissionRate ?? "10"),
      eligiblePlans: c.eligiblePlans || [],
      expiresAt: c.expiresAt ? c.expiresAt.slice(0, 10) : "",
    });
    setShowModal(true);
  };

  const handleFormChange = (field, val) => {
    if (field === "code") val = val.toUpperCase();
    setForm((p) => ({ ...p, [field]: val }));
  };

  const togglePlan = (plan) => {
    setForm((p) => ({
      ...p,
      eligiblePlans: p.eligiblePlans.includes(plan)
        ? p.eligiblePlans.filter((x) => x !== plan)
        : [...p.eligiblePlans, plan],
    }));
  };

  const handleSave = async () => {
    if (!form.code || !form.discountValue) {
      toast.warn("Code and discount value are required");
      return;
    }
    setSaving(true);
    const payload = {
      code: form.code,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      perUserLimit: form.perUserLimit ? Number(form.perUserLimit) : 1,
      influencer: form.influencer.trim() || undefined,
      upiHandle: form.upiHandle.trim() || undefined,
      commissionRate: form.commissionRate ? Number(form.commissionRate) : 10,
      eligiblePlans: form.eligiblePlans,
      expiresAt: form.expiresAt || undefined,
    };

    let res;
    if (editId) {
      res = await func.put(UPDATE_URL + editId, payload);
    } else {
      res = await func.post(CREATE_URL, payload);
    }

    if (res?.status === 200 || res?.status === 201) {
      toast.success(editId ? "Updated!" : "Created!");
      setShowModal(false);
      fetchCodes();
    } else {
      toast.error(res?.data?.message || "Operation failed");
    }
    setSaving(false);
  };

  const handleDeactivate = async (id) => {
    const res = await func.del(DELETE_URL + id);
    setConfirmDeactivate(null);
    if (res?.status === 200) {
      toast.success("Promo code deactivated");
      fetchCodes();
    } else {
      toast.error("Failed to deactivate");
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faTag} className="text-white text-lg" />
            <h1 className="text-xl font-bold text-white">Promo Codes</h1>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create New
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Code", "Type", "Value", "Max Uses", "Used", "Plans", "Expires", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(9)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : codes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-gray-400">No promo codes yet</td>
                  </tr>
                ) : (
                  codes.map((c) => (
                    <tr key={c._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 font-mono font-semibold text-gray-800">{c.code}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.discountType === "fixed" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                          {c.discountType === "fixed" ? "Flat" : "Percent"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        {c.discountType === "fixed" ? `₹${c.discountValue}` : `${c.discountValue}%`}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.maxUses}</td>
                      <td className="px-4 py-3 text-gray-600">{c.usedCount ?? 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.eligiblePlans || []).map((p) => (
                            <span key={p} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{p.replace("_", " ")}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(c.expiresAt)}</td>
                      <td className="px-4 py-3">
                        {c.active !== false ? (
                          <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                            <FontAwesomeIcon icon={faCheckCircle} /> Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-500 text-xs font-semibold">
                            <FontAwesomeIcon icon={faTimesCircle} /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(c)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </button>
                          {c.active !== false && (
                            <button
                              onClick={() => setConfirmDeactivate(c)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded transition"
                              title="Deactivate"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">
                {editId ? "Edit Promo Code" : "Create Promo Code"}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => handleFormChange("code", e.target.value)}
                  readOnly={!!editId}
                  placeholder="e.g. LAUNCH50"
                  className={`w-full text-sm px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-400 transition font-mono ${editId ? "bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200" : "border-gray-300"}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Discount Type *</label>
                  <select
                    value={form.discountType}
                    onChange={(e) => handleFormChange("discountType", e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition bg-white"
                  >
                    <option value="fixed">Flat (₹)</option>
                    <option value="percent">Percent (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Value * {form.discountType === "fixed" ? "(₹)" : "(%)"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.discountValue}
                    onChange={(e) => handleFormChange("discountValue", e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Total Uses</label>
                  <input
                    type="number"
                    min="1"
                    value={form.maxUses}
                    onChange={(e) => handleFormChange("maxUses", e.target.value)}
                    placeholder="Leave blank = unlimited"
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Per User Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={form.perUserLimit}
                    onChange={(e) => handleFormChange("perUserLimit", e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                  />
                </div>
              </div>

              {/* Influencer section */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-indigo-600 mb-2 uppercase tracking-wide">Influencer (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Handle / Name</label>
                    <input
                      type="text"
                      value={form.influencer}
                      onChange={(e) => handleFormChange("influencer", e.target.value)}
                      placeholder="@priya_vlogs"
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Commission Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={form.commissionRate}
                      onChange={(e) => handleFormChange("commissionRate", e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">UPI Handle (for auto-payout)</label>
                  <input
                    type="text"
                    value={form.upiHandle}
                    onChange={(e) => handleFormChange("upiHandle", e.target.value)}
                    placeholder="priya@ybl"
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">Handle enables Influencer Hub tracking + public dashboard link. UPI enables Saturday auto-payout scheduling.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Eligible Plans</label>
                <div className="flex flex-wrap gap-2">
                  {PLANS.map((p) => (
                    <label key={p} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition ${form.eligiblePlans.includes(p) ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                      <input
                        type="checkbox"
                        checked={form.eligiblePlans.includes(p)}
                        onChange={() => togglePlan(p)}
                        className="accent-indigo-600"
                      />
                      {p.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expires At (optional)</label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => handleFormChange("expiresAt", e.target.value)}
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 transition bg-white"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50"
              >
                {saving ? "Saving..." : editId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirm */}
      {confirmDeactivate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Deactivate Code?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Deactivate <strong className="font-mono">{confirmDeactivate.code}</strong>? Users won't be able to use it anymore.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeactivate(null)} className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={() => handleDeactivate(confirmDeactivate._id)} className="flex-1 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium">
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeList;
